import {Extension} from "@/types/extenions.ts";
import {api, cn} from "@/lib/utils.ts";
import {Button, buttonVariants} from "@/components/ui/button.tsx";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuTrigger
} from "@/components/ui/dropdown-menu.tsx";
import {CheckIcon, EllipsisVerticalIcon} from "@heroicons/react/24/outline";
import {useExtensionEdit} from "@/pages/extensions/components/ProviderExtensionEdit.tsx";
import {useDispatch, useSelector} from "@/store";
import {getState, reset} from "@/store/extensionsPageActions.ts";
import {
    useDuplicateExtensionMutation,
    useMoveExtensionMutation,
    useUpdateExtensionMutation
} from "@/store/extensions.ts";
import {toast} from "sonner";
import React, {useMemo, useState} from "react";
import {allowSubExtensions} from "@/pages/extensions/helpers.ts";
import {Spinner} from "@/components/ui/spinner.tsx";
import {ConfirmPopover} from "@/components/ui/ConfirmPopover.tsx";
import {ItemAction} from "@/pages/extensions/components/ActionsExtension.tsx";
import {useGlobalExtensionActions} from "@/pages/extensions/hooks/useGlobalExtensionsActions.ts";
import {Input} from "@/components/ui/input.tsx";


export function GetGlobalActions(props: {ext: Extension, isMain?: boolean}) {

    const [open, setOpen] = useState(false);
    const {ext, isMain} = props;
    const {isEditing, toggle} = useExtensionEdit(ext);
    const actions = useGlobalExtensionActions(ext, isMain);

    return (
        <>
            <CardExtensionGlobalToll {...props}/>
            <div className={cn(buttonVariants({variant: isEditing ? "default" : "outline", size:"sm"}), "gap-0 overflow-hidden cursor-pointer rounded-full max-h-6 p-0")}>
                <Button className={cn("cursor-pointer rounded-full max-h-6 px-2.5 border-none shadow-none ", !isEditing && "text-light-blue-800")} variant={isEditing ? "default" : "outline"} size="sm" onClick={toggle}>
                    {isEditing ? 'סגור חלון' : 'עריכה'}
                </Button>
                <DropdownMenu
                    dir="rtl"
                    modal={false}
                    open={open}
                    onOpenChange={setOpen}
                >
                    <DropdownMenuTrigger asChild>
                        <Button className={cn("cursor-pointer rounded-full max-h-6 w-6 border-none shadow-none ", !isEditing && "text-light-blue-800")} variant={isEditing ? "default" : "outline"} size="sm">
                            <EllipsisVerticalIcon className="!size-4 text-gray-400 transition"/>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="max-w-60">
                        <DropdownMenuGroup>
                            {actions.map((action) => (
                                <ItemAction
                                    key={action.action}
                                    action={action}
                                    ext={ext}
                                    setOpen={setOpen}
                                />
                            ))}
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </>
    )
}

function CardExtensionGlobalToll({ext, isMain}: {ext: Extension, isMain?: boolean}) {

    const {action, withSub, pinned: isPinned, extension} = useSelector(getState);
    const [updateExtension, {isLoading: isLoadingUpdate}] = useUpdateExtensionMutation();
    const [move, {isLoading: isLoadingMove}] = useMoveExtensionMutation();
    const [duplicate, {isLoading: isLoadingDuplicate}] = useDuplicateExtensionMutation();
    const dispatch = useDispatch();
    const [order, setOrder] = useState('');
    const [errors, setErrors] = useState<string>('');

    const isLoading = useMemo(() => {
       return {
          moving: isLoadingMove,
          copy_settings: isLoadingUpdate,
          duplicate: isLoadingDuplicate,
       }[action ?? 'copy_settings'] ?? false;
    }, [action, isLoadingDuplicate, isLoadingUpdate, isLoadingMove]);

    const body = useMemo(() => {
        if(!action || !extension) {
            setErrors('');
            setOrder('');
            return null;
        }

        if(action === 'moving' || action === 'duplicate') {
            return (
                <div className="pb-4">
                    <div className="space-y-2">
                        <label className="text-xs text-gray-500">
                            <span>הגדר מקש</span>
                            <Input
                                type="text"
                                value={order}
                                onChange={(e) => setOrder(e.target.value)}
                                placeholder="מקש"
                                className="w-full text-sm"
                            />
                            {errors && <small className="text-red-500 text-xs">{errors}</small>}
                        </label>
                    </div>
                </div>
            )
        }
        return null;
    }, [action, order, setOrder, errors, setErrors])

    function showTool() {
        if(!extension || !action || extension.id === ext.id) {
            return false;
        }

        return {
            moving: allowSubExtensions.includes(ext.cls_type),
            copy_settings: extension.cls_type === ext.cls_type,
            duplicate: allowSubExtensions.includes(ext.cls_type),
        }[action] ?? false;
    }

    if(!showTool() || !extension || !action) {
        return null;
    }

    const handleAction = async () =>  {

        if(action === 'copy_settings') {
            await updateExtension({
                ...ext,
                settings: {
                    ...ext.settings,
                    ...extension?.settings
                }
            })
                .unwrap()
                .then(() => {
                    toast.success(`הגדרות השלוחה הועתקו בהצלחה`);
                    if(!isPinned) {
                        dispatch(reset())
                    }
                }).catch(error => {
                    toast.error('לא הצלחנו להעתיק את ההגדרות...', {
                        description: error.data.msg,
                    })
                })
        }

        if(!order || Number(order) < 0) {
            setErrors('אנא הכנס מקש חוקי');
            return;
        }
        setErrors('');

        if(action === 'moving') {
            await move({
                id: extension.id,
                to: ext.id,
                order: Number(order),
                old_parent: extension.parent_id,
            })
                .unwrap()
                .then(() => {
                    toast.success(`השלוחה הועברה בהצלחה`);
                    if(!isPinned) {
                        dispatch(reset())
                    }
                }).catch(error => {
                    toast.error('לא הצלחנו להעביר את השלוחה...', {
                        description: error.data.msg,
                    })
                })
        }

        if(action === 'duplicate') {
            await duplicate({
                id: extension.id,
                order: Number(order),
                to: ext.id,
                with_sub: withSub,
            })
                .unwrap()
                .then(() => {
                    toast.success(`השלוחה שוכפלה בהצלחה`);
                    dispatch(reset())
                }).catch(error => {
                    toast.error('לא הצלחנו לשכפל את השלוחה...', {
                        description: error.data.msg,
                    })
                })
        }

    }

   const getMassage = () => ({
        moving: `האם אתה בטוח שאתה רוצה להעביר את ${ext.name} לכאן?`,
        copy_settings: `האם אתה בטוח שברצונך להעתיק את ההגדרות של ${extension.name} לשלוחה ${ext.name}?`,
        duplicate: `האם אתה בטוח שאתה רוצה לשכפל את ${ext.name} לכאן?`,
    })[action] ?? '';

    return (
        <>
            <div className="bg-light-blue-600/15 border-light-blue-600 shadow-lg border border-t-0 absolute top-0 left-[600px] rounded-b-lg overflow-hidden">
                <ConfirmPopover
                    onConfirm={handleAction}
                    message={getMassage()}
                    confirmText={`כן, ${{'moving': 'העבר לכאן', 'copy_settings': 'העתק', 'duplicate': 'שכפל לכאן'}[action]}`}
                    cancelText="לא, ביטול"
                    confirmButtonVariant="default"
                    cancelButtonVariant="outline"
                    buttonSize="sm"
                    body={body}
                >
                    <Button
                        className={cn("cursor-pointer max-h-7 w-7 border-none shadow-none hover:bg-light-blue-600 text-light-blue-600 hover:text-white rounded-none bg-transparent")} size="sm">
                        {isLoading
                            ? <Spinner size="sm" className="flex-shrink-0 border-light-blue-600/50"/>
                            : <CheckIcon className="!size-4 transition"/>}
                    </Button>
                </ConfirmPopover>
            </div>
        </>
    )
}